<template>
  <div class="identity-select">
    <div class="is-back" v-if="showBack" @click="$emit('back')">
      <i class="el-icon-back"></i> 返回上一步
    </div>
    <div class="is-head">
      <div class="is-title">{{ title }}</div>
      <div class="is-subtitle" v-if="subtitle">{{ subtitle }}</div>
    </div>
    <div class="is-cards">
      <div
        v-for="item in identities"
        :key="item.key"
        class="is-card"
        :class="item.className"
        @click="handleSelect(item)"
      >
        <div class="is-icon"><i :class="item.icon"></i></div>
        <div class="is-name">{{ item.name }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  /**
   * 身份选择组件（裁判员 / 培训员 / 学员）
   * 登录弹窗与注册流程共用。
   * 点击某张卡片后触发 select 事件，payload: { key, role, name }
   *  - key:  judge / trainer / student（用于注册路由地址）
   *  - role: '3' / '5' / '6'（登录、注册提交带上）
   */
  export default {
    name: 'IdentitySelect',
    props: {
      title: {
        type: String,
        default: '请选择您的身份'
      },
      subtitle: {
        type: String,
        default: '选择身份后进入对应操作'
      },
      showBack: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        identities: [
          {
            key: 'judge',
            role: '3',
            name: '裁判员',
            icon: 'el-icon-medal',
            className: 'judge'
          },
          {
            key: 'trainer',
            role: '5',
            name: '培训员',
            icon: 'el-icon-star-off',
            className: 'trainer'
          },
          {
            key: 'student',
            role: '6',
            name: '学员',
            icon: 'el-icon-user-solid',
            className: 'student'
          }
        ]
      }
    },
    methods: {
      handleSelect(item) {
        this.$emit('select', {
          key: item.key,
          role: item.role,
          name: item.name
        })
      }
    }
  }
</script>

<style lang="less" scoped>
  .identity-select {
    width: 100%;
    padding: 22px 26px 30px;
    background: linear-gradient(160deg, #eaf2ff 0%, #f6faff 45%, #fff7ec 100%);
    border-radius: 8px;
    position: relative;
    overflow: hidden;

    // 顶部装饰渐变条（蓝 → 橙）
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 6px;
      background: linear-gradient(90deg, #1e40af, #3b82f6 40%, #ff8f00 100%);
    }

    // 装饰圆
    &::after {
      content: '';
      position: absolute;
      right: -60px;
      top: -60px;
      width: 160px;
      height: 160px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(59, 130, 246, 0.18), transparent 68%);
    }
  }

  .is-head {
    text-align: center;
    margin-bottom: 26px;
    position: relative;
    z-index: 1;

    .is-title {
      font-size: 24px;
      font-weight: 800;
      color: #1e3a8a;
      letter-spacing: 1px;
    }

    .is-subtitle {
      margin-top: 8px;
      font-size: 13px;
      color: #64748b;
    }
  }

  /* 返回上一步 */
  .is-back {
    text-align: left;
    font-size: 14px;
    color: #64748b;
    cursor: pointer;
    margin-bottom: 4px;
    position: relative;
    z-index: 1;

    i {
      margin-right: 4px;
    }

    &:hover {
      color: var(--ts-primary, #1e40af);
    }
  }

  .is-cards {
    display: flex;
    justify-content: center;
    gap: 22px;
    flex-wrap: nowrap;
    position: relative;
    z-index: 1;
  }

  .is-card {
    flex: 1;
    max-width: 200px;
    padding: 34px 18px 28px;
    text-align: center;
    background: #ffffff;
    border: 1.5px solid #dbe4f2;
    border-radius: 18px;
    cursor: pointer;
    transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
    position: relative;
    box-shadow: 0 4px 14px rgba(30, 58, 138, 0.06);

    .is-icon {
      width: 74px;
      height: 74px;
      margin: 0 auto 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 22px;
      font-size: 34px;
      color: #ffffff;
      box-shadow: 0 10px 22px rgba(0, 0, 0, 0.14);
    }

    .is-name {
      font-size: 21px;
      font-weight: 800;
      color: #1f2937;
      margin-bottom: 9px;
    }

    .is-desc {
      font-size: 12.5px;
      color: #64748b;
      line-height: 1.6;
    }
  }

  .is-card:hover {
    transform: translateY(-7px);
    box-shadow: 0 18px 36px rgba(30, 58, 138, 0.18);
    border-color: transparent;
  }

  // 裁判员（跳橙，契合跳绳品牌）
  .is-card.judge .is-icon { background: linear-gradient(135deg, #f97316, #fb923c); }
  .is-card.judge:hover { box-shadow: 0 18px 36px rgba(249, 115, 22, 0.24); border-color: #fb923c; }

  // 培训员（绿）
  .is-card.trainer .is-icon { background: linear-gradient(135deg, #15803d, #22c55e); }
  .is-card.trainer:hover { box-shadow: 0 18px 36px rgba(34, 197, 94, 0.24); border-color: #22c55e; }

  // 学员（蓝）
  .is-card.student .is-icon { background: linear-gradient(135deg, #1e40af, #3b82f6); }
  .is-card.student:hover { box-shadow: 0 18px 36px rgba(59, 130, 246, 0.24); border-color: #3b82f6; }

  /* ============ 移动端适配：三张卡片改纵向排列，避免横向溢出被裁剪 ============ */
  @media (max-width: 768px) {
    .identity-select {
      padding: 18px 16px 22px;
    }

    .is-head {
      margin-bottom: 18px;

      .is-title {
        font-size: 19px;
        letter-spacing: 0.5px;
      }

      .is-subtitle {
        margin-top: 6px;
        font-size: 12.5px;
      }
    }

    .is-cards {
      flex-direction: column;
      gap: 12px;
    }

    .is-card {
      display: flex;
      align-items: center;
      max-width: 100%;
      padding: 14px 16px;
      border-radius: 14px;

      .is-icon {
        width: 52px;
        height: 52px;
        margin: 0 14px 0 0;
        border-radius: 14px;
        font-size: 24px;
        flex-shrink: 0;
      }

      .is-name {
        font-size: 17px;
        margin: 0;
      }
    }

    /* 触屏设备下去掉悬浮位移效果 */
    .is-card:hover {
      transform: none;
    }
  }
</style>
